import React, { Component } from "react";
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    Divider,
    InputAdornment,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";

import { Formik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import axios from "axios";

import config from "../../config.json";

class RegisterPage extends Component {
    apiEndPoint = config.apiEndPoint + "users/register";
    recaptchaRef = React.createRef();

    schema = Yup.object().shape({
        username: Yup.string()
            .required("Username is required")
            .min(4)
            .max(15),

        password: Yup.string()
            .required("Password is required")
            .min(4)
            .max(15),

        confirmPassword: Yup.string().oneOf(
            [Yup.ref("password"), null],
            "Passwords must match"
        ),

        email: Yup.string()
            .required("Email is required")
            .email("Enter a valid email"),
    });

    async doSubmit(values, captchaResponse) {
        try {
            await axios.post(this.apiEndPoint, {
                ...values,
                captchaResponse,
            });

            toast.success("Account created successfully!");

            setTimeout(() => {
                window.location = "/downloads";
            }, 2500);
        } catch (ex) {
            toast.error(
                ex?.response?.data || "Registration failed"
            );
            this.recaptchaRef.current?.reset();
        }
    }

    render() {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: { xs: 5, md: 8 },
                    px: { xs: 2, sm: 3 },
                    background:
                        "radial-gradient(circle at 12% 8%, rgba(57,217,138,0.12), transparent 32%), radial-gradient(circle at 92% 90%, rgba(57,217,138,0.08), transparent 30%), linear-gradient(145deg, #06120A 0%, #08180E 48%, #030805 100%)",
                    color: "#F1FFF5",

                    "&:before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background:
                            "linear-gradient(90deg, transparent, #39D98A, #9BFFC4, #39D98A, transparent)",
                    },
                }}
            >
                {/* TOP GREEN GLOW */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-250px",
                        left: "-250px",
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.13) 0%, transparent 70%)",
                        filter: "blur(100px)",
                        pointerEvents: "none",
                    }}
                />

                {/* BOTTOM GLOW */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-300px",
                        right: "-250px",
                        width: 650,
                        height: 650,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.08) 0%, transparent 70%)",
                        filter: "blur(110px)",
                        pointerEvents: "none",
                    }}
                />

                {/* CENTER LIGHT */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "45%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.04), transparent 70%)",
                        filter: "blur(100px)",
                        pointerEvents: "none",
                    }}
                />

                {/* SUBTLE BACKGROUND GRID */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.12,
                        pointerEvents: "none",
                        backgroundImage:
                            "linear-gradient(rgba(155,255,196,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(155,255,196,0.07) 1px, transparent 1px)",
                        backgroundSize: "65px 65px",
                        maskImage:
                            "linear-gradient(to bottom, black, transparent 85%)",
                    }}
                />

                <Container
                    maxWidth="sm"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: {
                                xs: "22px",
                                sm: "28px",
                            },
                            background:
                                "linear-gradient(150deg, rgba(13,35,22,0.98), rgba(7,19,12,0.99) 55%, rgba(4,11,7,0.99))",
                            border:
                                "1px solid rgba(57,217,138,0.20)",
                            boxShadow:
                                "0 30px 90px rgba(0,0,0,0.65), 0 0 55px rgba(57,217,138,0.04)",

                            "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "8%",
                                right: "8%",
                                height: "2px",
                                background:
                                    "linear-gradient(90deg, transparent, #39D98A, #9BFFC4, #39D98A, transparent)",
                            },
                        }}
                    >
                        {/* HEADER */}
                        <Box
                            sx={{
                                position: "relative",
                                textAlign: "center",
                                py: {
                                    xs: 4,
                                    md: 5,
                                },
                                px: 3,
                                overflow: "hidden",
                                borderBottom:
                                    "1px solid rgba(57,217,138,0.12)",
                                background:
                                    "linear-gradient(145deg, rgba(57,217,138,0.075), rgba(8,20,12,0.65))",
                            }}
                        >
                            {/* HEADER GLOW */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "-120px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: 350,
                                    height: 300,
                                    borderRadius: "50%",
                                    background:
                                        "radial-gradient(circle, rgba(57,217,138,0.14), transparent 70%)",
                                    filter: "blur(70px)",
                                    pointerEvents: "none",
                                }}
                            />

                            {/* ICON */}
                            <Box
                                sx={{
                                    position: "relative",
                                    width: {
                                        xs: 68,
                                        md: 78,
                                    },
                                    height: {
                                        xs: 68,
                                        md: 78,
                                    },
                                    mx: "auto",
                                    mb: 2.5,
                                    borderRadius: "22px",
                                    background:
                                        "linear-gradient(145deg, #9BFFC4, #39D98A 52%, #13864F)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow:
                                        "0 0 0 6px rgba(57,217,138,0.06), 0 15px 45px rgba(57,217,138,0.18)",
                                    transform: "rotate(-4deg)",
                                }}
                            >
                                <HowToRegRoundedIcon
                                    sx={{
                                        fontSize: {
                                            xs: 34,
                                            md: 40,
                                        },
                                        color: "#062112",
                                        transform: "rotate(4deg)",
                                    }}
                                />
                            </Box>

                            {/* SMALL LABEL */}
                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "#9BFFC4",
                                    fontWeight: 900,
                                    letterSpacing: "3px",
                                    fontSize: "10px",
                                    mb: 1.5,
                                }}
                            >
                                CRITICAL CONQUER • CLASSIC 5165
                            </Typography>

                            {/* TITLE */}
                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "#F1FFF5",
                                    fontWeight: 950,
                                    fontSize: {
                                        xs: "29px",
                                        sm: "35px",
                                        md: "40px",
                                    },
                                    lineHeight: 1.05,
                                    letterSpacing: "-1px",
                                }}
                            >
                                CREATE YOUR
                            </Typography>

                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "#39D98A",
                                    fontWeight: 950,
                                    fontSize: {
                                        xs: "29px",
                                        sm: "35px",
                                        md: "40px",
                                    },
                                    lineHeight: 1.1,
                                    letterSpacing: "-1px",
                                }}
                            >
                                LEGACY
                            </Typography>

                            <Typography
                                sx={{
                                    position: "relative",
                                    color: "rgba(222,255,233,0.60)",
                                    mt: 1.8,
                                    fontSize: {
                                        xs: "12px",
                                        md: "13px",
                                    },
                                    lineHeight: 1.8,
                                }}
                            >
                                Create your account and prepare for
                                <br />
                                a return to the classic era.
                            </Typography>

                            {/* CLASSIC SERVER LABEL */}
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 2.5,
                                    px: 2,
                                    py: 0.9,
                                    borderRadius: "30px",
                                    border:
                                        "1px solid rgba(57,217,138,0.20)",
                                    background:
                                        "rgba(57,217,138,0.055)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: "50%",
                                        background: "#39D98A",
                                        boxShadow:
                                            "0 0 12px rgba(57,217,138,0.7)",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: "#B8ECCD",
                                        fontSize: "10px",
                                        fontWeight: 800,
                                        letterSpacing: "1.5px",
                                    }}
                                >
                                    YOUR ADVENTURE STARTS HERE
                                </Typography>
                            </Box>
                        </Box>

                        {/* FORM */}
                        <Box
                            sx={{
                                p: {
                                    xs: 3,
                                    sm: 4,
                                    md: 5,
                                },
                            }}
                        >
                            <Formik
                                validationSchema={this.schema}
                                initialValues={{
                                    username: "",
                                    password: "",
                                    confirmPassword: "",
                                    email: "",
                                }}
                                onSubmit={(values) =>
                                    this.doSubmit(
                                        values,
                                        this.recaptchaRef.current?.getValue()
                                    )
                                }
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                }) => (
                                    <form onSubmit={handleSubmit}>
                                        {/* FORM LABEL */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                mb: 3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 4,
                                                    height: 24,
                                                    borderRadius: "10px",
                                                    background: "#39D98A",
                                                    boxShadow:
                                                        "0 0 12px rgba(57,217,138,0.35)",
                                                }}
                                            />

                                            <Box>
                                                <Typography
                                                    sx={{
                                                        color: "#F1FFF5",
                                                        fontWeight: 900,
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    Account Information
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color:
                                                            "rgba(222,255,233,0.43)",
                                                        fontSize: "11px",
                                                        mt: 0.4,
                                                    }}
                                                >
                                                    Fill in the details below to register.
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* USERNAME */}
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            name="username"
                                            value={values.username}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="username"
                                            inputProps={{
                                                maxLength: 15,
                                            }}
                                            sx={fieldStyle}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonOutlineIcon
                                                            sx={{
                                                                color: "#39D98A",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.username &&
                                            touched.username && (
                                                <ErrorText>
                                                    {errors.username}
                                                </ErrorText>
                                            )}

                                        {/* EMAIL */}
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="email"
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailOutlinedIcon
                                                            sx={{
                                                                color: "#39D98A",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.email &&
                                            touched.email && (
                                                <ErrorText>
                                                    {errors.email}
                                                </ErrorText>
                                            )}

                                        {/* PASSWORD */}
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Password"
                                            name="password"
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="new-password"
                                            inputProps={{
                                                maxLength: 15,
                                            }}
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                color: "#39D98A",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.password &&
                                            touched.password && (
                                                <ErrorText>
                                                    {errors.password}
                                                </ErrorText>
                                            )}

                                        {/* CONFIRM PASSWORD */}
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Confirm Password"
                                            name="confirmPassword"
                                            value={values.confirmPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="new-password"
                                            inputProps={{
                                                maxLength: 15,
                                            }}
                                            sx={{
                                                ...fieldStyle,
                                                mt: 2.5,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                color: "#39D98A",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {errors.confirmPassword &&
                                            touched.confirmPassword && (
                                                <ErrorText>
                                                    {errors.confirmPassword}
                                                </ErrorText>
                                            )}

                                        {/* DIVIDER */}
                                        <Divider
                                            sx={{
                                                my: 3.5,
                                                borderColor:
                                                    "rgba(155,255,196,0.09)",
                                            }}
                                        />

                                        {/* CAPTCHA */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minHeight: 78,
                                                p: 1.5,
                                                borderRadius: "14px",
                                                background:
                                                    "rgba(255,255,255,0.018)",
                                                border:
                                                    "1px solid rgba(155,255,196,0.09)",
                                                overflow: "hidden",
                                                "& > div": {
                                                    maxWidth: "100%",
                                                },
                                            }}
                                        >
                                            <ReCAPTCHA
                                                ref={this.recaptchaRef}
                                                sitekey={
                                                    config.RECAPTCHA_SITE_KEY
                                                }
                                            />
                                        </Box>

                                        {/* CREATE ACCOUNT BUTTON */}
                                        <Button
                                            type="submit"
                                            fullWidth
                                            sx={{
                                                mt: 3,
                                                py: 1.7,
                                                borderRadius: "14px",
                                                background:
                                                    "linear-gradient(135deg, #9BFFC4 0%, #39D98A 50%, #15985A 100%)",
                                                color: "#062112",
                                                fontWeight: 950,
                                                fontSize: "13px",
                                                letterSpacing: "1px",
                                                textTransform: "uppercase",
                                                boxShadow:
                                                    "0 12px 30px rgba(57,217,138,0.14), inset 0 1px 0 rgba(255,255,255,0.22)",
                                                transition:
                                                    "all 0.25s ease",

                                                "&:hover": {
                                                    background:
                                                        "linear-gradient(135deg, #C4FFDA 0%, #70F0A8 50%, #39D98A 100%)",
                                                    transform:
                                                        "translateY(-2px)",
                                                    boxShadow:
                                                        "0 16px 38px rgba(57,217,138,0.23)",
                                                },

                                                "&:active": {
                                                    transform:
                                                        "translateY(0)",
                                                },
                                            }}
                                        >
                                            Create Account
                                        </Button>

                                        {/* LOGIN */}
                                        <Typography
                                            sx={{
                                                textAlign: "center",
                                                mt: 3,
                                                color:
                                                    "rgba(222,255,233,0.48)",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Already have an account?{" "}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: "#9BFFC4",
                                                    fontWeight: 900,
                                                    cursor: "pointer",
                                                    ml: 0.5,
                                                    transition: "0.2s",

                                                    "&:hover": {
                                                        color: "#FFFFFF",
                                                    },
                                                }}
                                                onClick={() =>
                                                (window.location =
                                                    "/user/login")
                                                }
                                            >
                                                Login
                                            </Box>
                                        </Typography>
                                    </form>
                                )}
                            </Formik>
                        </Box>

                        {/* BOTTOM BRAND STRIP */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2.5,
                                textAlign: "center",
                                borderTop:
                                    "1px solid rgba(155,255,196,0.08)",
                                background: "rgba(0,0,0,0.2)",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "rgba(155,255,196,0.42)",
                                    fontSize: "9px",
                                    fontWeight: 800,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                }}
                            >
                                Critical Conquer • Version 5165
                            </Typography>

                            <Typography
                                sx={{
                                    color:
                                        "rgba(222,255,233,0.24)",
                                    fontSize: "10px",
                                    mt: 1,
                                }}
                            >
                                Begin your journey. Build your legacy.
                            </Typography>
                        </Box>
                    </Paper>

                    {/* PAGE FOOTER */}
                    <Typography
                        sx={{
                            mt: 3,
                            textAlign: "center",
                            color: "rgba(222,255,233,0.28)",
                            fontSize: "10px",
                            letterSpacing: "1.5px",
                        }}
                    >
                        YOUR LEGEND. YOUR REALM. YOUR ADVENTURE.
                    </Typography>
                </Container>
            </Box>
        );
    }
}

/* =========================================================
   FIELD STYLE
========================================================= */

const fieldStyle = {
    "& .MuiOutlinedInput-root": {
        minHeight: 54,
        background:
            "linear-gradient(145deg, rgba(255,255,255,0.035), rgba(255,255,255,0.018))",
        borderRadius: "14px",
        color: "#F1FFF5",
        transition: "all 0.2s ease",

        "& fieldset": {
            borderColor: "rgba(155,255,196,0.13)",
        },

        "&:hover": {
            background:
                "linear-gradient(145deg, rgba(57,217,138,0.045), rgba(255,255,255,0.018))",

            "& fieldset": {
                borderColor: "rgba(57,217,138,0.4)",
            },
        },

        "&.Mui-focused": {
            background: "rgba(57,217,138,0.035)",

            "& fieldset": {
                borderColor: "#39D98A",
                borderWidth: "1px",
            },
        },
    },

    "& .MuiInputLabel-root": {
        color: "rgba(222,255,233,0.48)",
        fontSize: "13px",

        "&.Mui-focused": {
            color: "#9BFFC4",
        },
    },

    "& .MuiInputLabel-shrink": {
        background: "#0B1D13",
        padding: "0 5px",
    },

    "& input": {
        color: "#F1FFF5",
        fontSize: "13px",
    },
};

/* =========================================================
   ERROR STYLE
========================================================= */

const ErrorText = ({ children }) => (
    <Typography
        sx={{
            color: "#FF7777",
            fontSize: "11px",
            mt: 0.8,
            ml: 1,
            fontWeight: 600,
        }}
    >
        {children}
    </Typography>
);

export default RegisterPage;